import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import { TriggerTimeType } from '../../../app/types';
import { changeSeconds, generateDateItem } from '../../../app/utils';
import { setNodeData } from '../../../features/mainSlice';
import Dropdown from '../../shared/Dropdown';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 12,
    },
    block: {
      display: 'flex',
      alignItems: 'center',
    },
    option: {
      marginRight: 36,
      marginBottom: 12,
    },
    radioInput: {
      fontSize: 15,
    },
    radio: {
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$13,
    },
    dropdown: {
      width: 80,
      marginRight: 8,
    },
    time: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$13,
    },
  },
  {
    name: 'Time',
  },
);

const dayItem = generateDateItem(29, 1, 1);

const hourItem = generateDateItem(22, 1, 1);

const minuteItem = generateDateItem(58, 1, 1);

const Time = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [radioType, setRadioType] = useState(TriggerTimeType.DAY);
  const [day, setDay] = useState(1);
  const [hour, setHour] = useState(1);
  const [minute, setMinute] = useState(1);
  const { selectedNode } = useSelector(state => state.ma);
  const { timeType, time } = selectedNode?.data;

  const onChange = value => {
    if (value === TriggerTimeType.DAY) {
      dispatch(
        setNodeData({
          timeType: value,
          time: 86400,
        }),
      );
    } else if (value === TriggerTimeType.HOUR) {
      dispatch(
        setNodeData({
          timeType: value,
          time: 3600,
        }),
      );
    } else {
      dispatch(
        setNodeData({
          timeType: value,
          time: 60,
        }),
      );
    }
  };

  const onDayChange = value => {
    dispatch(
      setNodeData({
        time: parseInt(value || 0, 10) * 24 * 60 * 60,
      }),
    );
  };

  const onHourChange = value => {
    dispatch(
      setNodeData({
        time: parseInt(value || 0, 10) * 60 * 60,
      }),
    );
  };

  const onMinuteChange = value => {
    dispatch(
      setNodeData({
        time: parseInt(value || 0, 10) * 60,
      }),
    );
  };

  useEffect(() => {
    setRadioType(timeType);
  }, [timeType]);

  useEffect(() => {
    if (time) {
      if (timeType === TriggerTimeType.DAY) {
        setDay(changeSeconds(time, timeType) || 30);
        setHour(1);
        setMinute(1);
      }
      if (timeType === TriggerTimeType.HOUR) {
        setHour(changeSeconds(time, timeType));
        setDay(1);
        setMinute(1);
      }
      if (timeType === TriggerTimeType.MINUTE) {
        setMinute(changeSeconds(time, timeType));
        setDay(1);
        setHour(1);
      }
    }
  }, [time, timeType]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t('condition-setting.time')}</div>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="default-display"
          name="default-display"
          value={radioType}
          onChange={e => {
            e.stopPropagation();
            onChange(e?.target?.value);
          }}
        >
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={TriggerTimeType.MINUTE}
            control={<Radio color="primary" />}
            label={
              <div className={classes.time}>
                <Dropdown
                  className={classes.dropdown}
                  menuItems={minuteItem}
                  value={minute}
                  onChange={onMinuteChange}
                  disabled={timeType !== TriggerTimeType.MINUTE}
                />
                {t('condition-setting.minute')}
              </div>
            }
          />
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={TriggerTimeType.HOUR}
            control={<Radio color="primary" />}
            label={
              <div className={classes.time}>
                <Dropdown
                  className={classes.dropdown}
                  menuItems={hourItem}
                  value={hour}
                  onChange={onHourChange}
                  disabled={timeType !== TriggerTimeType.HOUR}
                />
                {t('condition-setting.hour')}
              </div>
            }
          />
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={TriggerTimeType.DAY}
            control={<Radio color="primary" />}
            label={
              <div className={classes.time}>
                <Dropdown
                  className={classes.dropdown}
                  menuItems={dayItem}
                  value={day}
                  onChange={onDayChange}
                  disabled={timeType !== TriggerTimeType.DAY}
                />
                {t('condition-setting.day')}
              </div>
            }
          />
        </RadioGroup>
      </FormControl>
    </div>
  );
};

export default Time;
